import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowRight,
  Briefcase,
  Building2,
  CalendarClock,
  ClipboardList,
  History,
  MapPin,
  Radio,
  ShieldCheck,
  Users,
  UsersRound,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { api } from "@/lib/api";
import type { ManagedUser, Role } from "@/types";
import { Card } from "@/components/ui/Card";
import { describeAccion } from "@/lib/describeAccion";

const roleLabels: Record<Role, string> = {
  SUPER_ADMIN: "Super-admin",
  PROVIDER: "Proveedor",
  CLUB: "Usuario de club",
  REFEREE: "Árbitro",
};

const ROLE_COLORS: Record<Role, string> = {
  SUPER_ADMIN: "var(--color-chart-blue)",
  PROVIDER: "var(--color-chart-orange)",
  CLUB: "var(--color-chart-aqua)",
  REFEREE: "var(--color-chart-sky)",
};

const weekdayFormatter = new Intl.DateTimeFormat("es-ES", { weekday: "short" });
const dateTimeFormatter = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const relativeFormatter = new Intl.RelativeTimeFormat("es-ES", { numeric: "auto" });
const todayFormatter = new Intl.DateTimeFormat("es-ES", { weekday: "long", day: "numeric", month: "long" });

function relativeTime(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  if (Math.abs(diffMin) < 60) return relativeFormatter.format(diffMin, "minute");
  const diffHour = Math.round(diffMin / 60);
  if (Math.abs(diffHour) < 24) return relativeFormatter.format(diffHour, "hour");
  return relativeFormatter.format(Math.round(diffHour / 24), "day");
}

interface RecentActivity {
  id: number;
  accion: string;
  createdAt: string;
  usuario: { nombre: string; apellidos: string | null } | null;
}

interface UpcomingReservation {
  id: number;
  startsAt: string;
  customerName: string;
  courtLabel: string;
  club: { nombre: string };
}

interface DashboardStats {
  reservationsToday: number;
  liveMatchesNow: number;
  matchesLast7Days: number;
  reservationsLast7Days: { date: string; count: number }[];
  clubsTotal: number;
  clubsActive: number;
  courtsTotal: number;
  playersTotal: number;
  providersTotal: number | null;
  pendingRegistrations: number | null;
  // Opcionales de verdad: si el backend desplegado todavía no trae estos dos campos (p.ej.
  // justo durante un redeploy), la página no debe reventar por eso — se tratan como [].
  recentActivity?: RecentActivity[];
  upcomingReservations?: UpcomingReservation[];
}

function StatCard({
  icon: Icon,
  label,
  value,
  sublabel,
  tone,
}: {
  icon: typeof Users;
  label: string;
  value: string;
  sublabel?: string;
  tone: "brand" | "sky" | "amber" | "violet" | "rose";
}) {
  const tones = {
    brand: "bg-brand-50 text-brand-600",
    sky: "bg-sky-50 text-sky-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
    rose: "bg-rose-50 text-rose-600",
  } as const;

  return (
    <Card className="flex items-center gap-4">
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-xs font-medium text-ink-400">{label}</p>
        <p className="text-xl font-bold text-ink-900">{value}</p>
        {sublabel && <p className="text-xs text-ink-300">{sublabel}</p>}
      </div>
    </Card>
  );
}

const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid #e5e8ec",
  fontSize: 13,
  boxShadow: "0 4px 16px rgba(11,11,11,0.08)",
};

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get<{ data: ManagedUser[] }>("/users?limit=100")).data.data,
  });

  const { data: stats } = useQuery({
    queryKey: ["dashboard", "stats"],
    queryFn: async () => (await api.get<DashboardStats>("/dashboard/stats")).data,
    refetchInterval: 60_000,
  });

  const roleData = useMemo(() => {
    const counts: Record<Role, number> = { SUPER_ADMIN: 0, PROVIDER: 0, CLUB: 0, REFEREE: 0 };
    for (const u of users ?? []) counts[u.role]++;
    return (Object.keys(counts) as Role[])
      .filter((r) => counts[r] > 0)
      .map((r) => ({ rol: roleLabels[r], value: counts[r], role: r }));
  }, [users]);

  const reservationsChartData = useMemo(
    () =>
      (stats?.reservationsLast7Days ?? []).map((d) => ({
        dia: weekdayFormatter.format(new Date(`${d.date}T12:00:00`)),
        reservas: d.count,
      })),
    [stats]
  );

  const firstName = user?.nombre?.split(" ")[0] ?? "";
  const today = todayFormatter.format(new Date());

  return (
    <div className="flex flex-col gap-8">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 p-7 text-white shadow-lg shadow-brand-900/20">
        <div className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-20 right-24 h-40 w-40 rounded-full bg-white/5" />
        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-sm font-medium tracking-wide text-brand-100 uppercase">{today}</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight">
              Hola, {firstName}
            </h1>
            <p className="mt-2 max-w-md text-sm text-brand-50/90">
              {isSuperAdmin
                ? "Esto es lo que está pasando ahora mismo en toda la plataforma."
                : user?.role === "PROVIDER"
                  ? `Esto es lo que está pasando ahora mismo en ${user.ownedClubs?.length === 1 ? "tu club" : "tus clubes"}.`
                  : `Esto es lo que está pasando ahora mismo en ${user?.club?.nombre ?? "tu club"}.`}
            </p>
          </div>
          <div className="flex gap-6 text-right">
            <div>
              <p className="text-2xl font-bold">{stats ? stats.reservationsToday : "—"}</p>
              <p className="text-xs text-brand-100">Reservas hoy</p>
            </div>
            <div>
              <p className="flex items-center justify-end gap-1.5 text-2xl font-bold">
                {stats && stats.liveMatchesNow > 0 && <span className="h-2 w-2 animate-pulse rounded-full bg-white" />}
                {stats ? stats.liveMatchesNow : "—"}
              </p>
              <p className="text-xs text-brand-100">En directo ahora</p>
            </div>
          </div>
        </div>
      </div>

      {isSuperAdmin && stats && stats.pendingRegistrations !== null && stats.pendingRegistrations > 0 && (
        <button
          onClick={() => navigate("/proveedores")}
          className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3.5 text-left transition-colors hover:bg-amber-100"
        >
          <span className="flex items-center gap-3 text-sm font-medium text-amber-800">
            <ClipboardList className="h-4 w-4 shrink-0" />
            Tienes {stats.pendingRegistrations} solicitud{stats.pendingRegistrations === 1 ? "" : "es"} de alta de proveedor sin revisar
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-amber-600" />
        </button>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Building2}
          label="Clubes"
          value={stats ? `${stats.clubsActive}/${stats.clubsTotal}` : "—"}
          sublabel="activos / total"
          tone="brand"
        />
        <StatCard icon={MapPin} label="Pistas" value={stats ? String(stats.courtsTotal) : "—"} tone="sky" />
        <StatCard icon={UsersRound} label="Jugadores registrados" value={stats ? String(stats.playersTotal) : "—"} tone="violet" />
        {isSuperAdmin ? (
          <StatCard icon={Briefcase} label="Proveedores activos" value={stats?.providersTotal !== null && stats ? String(stats.providersTotal) : "—"} tone="amber" />
        ) : (
          <StatCard icon={Radio} label="Partidos en directo ahora" value={stats ? String(stats.liveMatchesNow) : "—"} tone="rose" />
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-ink-900">Reservas de los últimos 7 días</h2>
              <p className="text-xs text-ink-400">
                {stats ? `${stats.matchesLast7Days} partido${stats.matchesLast7Days === 1 ? "" : "s"} jugado(s) en este periodo` : "Cargando…"}
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={reservationsChartData} margin={{ left: -20 }}>
              <XAxis
                dataKey="dia"
                tickLine={false}
                axisLine={{ stroke: "#c3c2b7" }}
                tick={{ fill: "#898781", fontSize: 12 }}
              />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: "#898781", fontSize: 12 }} width={30} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#f4f6f8" }} contentStyle={tooltipStyle} />
              <Bar dataKey="reservas" name="Reservas" fill="var(--color-chart-blue)" radius={[4, 4, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="lg:col-span-2">
          <h2 className="mb-4 font-semibold text-ink-900">Usuarios por rol</h2>
          {roleData.length === 0 ? (
            <p className="text-sm text-ink-400">Todavía no hay usuarios.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={roleData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="rol"
                  tickLine={false}
                  axisLine={false}
                  width={100}
                  tick={{ fill: "#3c4551", fontSize: 12 }}
                />
                <Tooltip cursor={{ fill: "#f4f6f8" }} contentStyle={tooltipStyle} />
                <Bar dataKey="value" name="Usuarios" radius={[0, 4, 4, 0]} maxBarSize={28} label={{ position: "right", fill: "#3c4551", fontSize: 12 }}>
                  {roleData.map((entry) => (
                    <Cell key={entry.role} fill={ROLE_COLORS[entry.role]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-brand-600" />
            <h2 className="font-semibold text-ink-900">Próximas partidas</h2>
          </div>
          {!stats ? (
            <p className="text-sm text-ink-400">Cargando…</p>
          ) : (stats.upcomingReservations ?? []).length === 0 ? (
            <p className="text-sm text-ink-400">No hay próximas reservas confirmadas.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {(stats.upcomingReservations ?? []).map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-ink-50/60">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink-800">{r.customerName}</p>
                    <p className="truncate text-xs text-ink-400">
                      {r.club.nombre} — {r.courtLabel}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-ink-500">{dateTimeFormatter.format(new Date(r.startsAt))}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center gap-2">
            <History className="h-4 w-4 text-brand-600" />
            <h2 className="font-semibold text-ink-900">Actividad reciente</h2>
          </div>
          {!stats ? (
            <p className="text-sm text-ink-400">Cargando…</p>
          ) : (stats.recentActivity ?? []).length === 0 ? (
            <p className="text-sm text-ink-400">Todavía no hay ninguna acción registrada.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {(stats.recentActivity ?? []).map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-ink-50/60">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink-800">{describeAccion(a.accion)}</p>
                    <p className="truncate text-xs text-ink-400">
                      {a.usuario ? `${a.usuario.nombre} ${a.usuario.apellidos ?? ""}`.trim() : "Sistema"}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-ink-400">{relativeTime(a.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={() => navigate("/auditoria")}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold text-brand-600 transition-colors hover:bg-brand-50"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Ver todos los logs
          </button>
        </Card>
      </div>
    </div>
  );
}
